import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { makeStyles } from '@mui/styles';
import { styled } from '@mui/material/styles';
import { Stack, Theme, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { PricingSheet } from '../../types/PricingSheet';
import { I18nKeys } from '../../constants/I18nKeys';
import { matchExists } from '../../utils/searchUtils';
import { CellMetadata, ClientDataTableRowDiff } from '../../types/ClientData';
import { AppState } from '../../types/AppState';
import { getPricingSheetLabelParts } from '../../utils/pricingSheetUtils';
import { AttributesButton, PricingAttribute } from './PricingAttribute';
import { SupplierDataCategory } from '../../constants/VendorData';
import { setSelectedPricingSheetId, setSelectedSizeBasedPricingSheetId } from '../../ducks/pricingSlice';
import { SalesViewUILayout } from '../../constants/Viewer';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    borderRight: `1.5px solid #DADADA`,
    height: 'auto',
    width: '25%',
    minWidth: '288px',
  },
  searchField: {
    padding: '0px 0px 6px',
    color: theme.palette.getContrastText(theme.palette.secondary.main),
    '& .MuiInputBase-root': {
      borderRadius: '8px',
    },
    '& input': {
      padding: '10px 0px',
    },
  },
  stack: {
    flex: 1,
    height: '100%',
    padding: `${theme.spacing(2)} ${theme.spacing(2)}`,
    overflow: 'auto',
  },
  list: {
    textAlign: 'start',
    padding: `${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(4)}`,
    margin: 0,
  },
  toolTipList: {
    textAlign: 'start',
    padding: `${theme.spacing(2)}`,
    margin: 0,
  },
  sheetTitle: {
    position: 'absolute',
    bottom: theme.spacing(1),
    right: theme.spacing(1),
  },
  attributes: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
  },
  sidingUpcharge: {
    color: '#38761E',
    fontWeight: 'bold',
  },
}));

const AttributesList = styled(Stack)({
  position: 'absolute',
  top: 0,
  bottom: '-24px',
  paddingBottom: '24px',
  width: '264px',
  height: 'calc(100% + 24px)',
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'none',
});

type Props = {
  selectedPricingSheet?: PricingSheet;
  pricingSheets: PricingSheet[];
  cellMetadataDiff: CellMetadata[];
  pricingDiffs: {
    table: string;
    changes: ClientDataTableRowDiff[];
  }[];
};

export const PricingAttributes: React.FC<Props> = ({
  selectedPricingSheet: initialSelectedPricingSheet,
  pricingSheets,
  cellMetadataDiff,
  pricingDiffs,
}: Props) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [filteredPricingBaseSheets, setFilteredPricingBaseSheets] = useState<PricingSheet[]>([]);

  const searchTerm = useAppSelector((state: AppState) => state?.search?.searchTerm || '');
  const pricingTab = useAppSelector((state: AppState) => state?.viewer?.selectedPricingTabId || '');
  const secondaryMenuItem = useAppSelector((state: AppState) => state?.menu?.secondaryMenuItem);

  useEffect(() => {
    setFilteredPricingBaseSheets(
      !searchTerm
        ? pricingSheets
        : pricingSheets.filter((pricingSheet) =>
            getPricingSheetLabelParts(pricingSheet, SalesViewUILayout.Horizon, pricingTab, secondaryMenuItem, t).some(
              (label) => label && matchExists(label, searchTerm, {}),
            ),
          ),
    );
  }, [pricingSheets, initialSelectedPricingSheet, pricingTab, searchTerm, t, secondaryMenuItem]);

  const onClick = useCallback(
    (id: string) => {
      switch (secondaryMenuItem) {
        case SupplierDataCategory.BasePrices:
          dispatch(setSelectedPricingSheetId(id));
          break;
        case SupplierDataCategory.SizeBased:
          dispatch(setSelectedSizeBasedPricingSheetId(id));
          break;
        default:
          break;
      }
    },
    [secondaryMenuItem, dispatch],
  );

  return (
    <AttributesList direction="column" justifyContent="flex-start" alignItems="stretch">
      {!pricingSheets.length && (
        <AttributesButton className={classes.list}>
          <Typography className={classes.attributes} variant="body2">
            {t(I18nKeys.PricingBaseAttributeEmpty)}
          </Typography>
        </AttributesButton>
      )}
      {!!pricingSheets.length && !filteredPricingBaseSheets.length && (
        <Typography
          className={classes.attributes}
          variant="body2"
          color="text.secondary"
          style={{ textAlign: 'center' }}
        >
          {t(I18nKeys.PricingBaseAttributeNoneFound)}
        </Typography>
      )}
      {!!filteredPricingBaseSheets.length &&
        filteredPricingBaseSheets
          .slice(0)
          .sort((a) => (a.id === initialSelectedPricingSheet?.id ? -1 : 1))
          .map((sheet) => {
            const selected = sheet.id === initialSelectedPricingSheet?.id;
            return (
              <motion.div
                key={sheet.id}
                layout
                layoutId={`pricing-attribute-${sheet.id}`}
                initial={false}
                animate={
                  selected
                    ? { position: 'sticky', top: 0, left: 0, zIndex: 1000 }
                    : { position: 'relative', top: 0, left: 0, zIndex: 1 }
                }
                transition={{
                  duration: 0.75,
                  ease: 'easeInOut',
                }}
                style={{
                  willChange: 'transform',
                  ...(selected
                    ? { position: 'sticky', top: 0, left: 0, zIndex: 1000 }
                    : { position: 'relative', top: 0, left: 0 }),
                }}
                onClick={() => onClick(sheet.id)}
              >
                <PricingAttribute
                  selected={selected}
                  pricingSheet={sheet}
                  cellMetadataDiff={cellMetadataDiff}
                  pricingDiffs={pricingDiffs}
                  onClick={onClick}
                />
              </motion.div>
            );
          })}
    </AttributesList>
  );
};
